import type { ManifestObjectAppearance } from "daedalus-core";
import { CharacterState, CoinSound, EntityKind, HEAL_SOUND_SET, HitFlag, InterfaceSound, ODYSSEY_ADAPTER, RESTART_BIT, SoundActor, Stinger, abilityByNumber, abilityHook, characterStateOf, creatureHook, drinkVariantOfState, itemHook, npcHook, playerCombatHook, SIXTEENTHS, objectTypeOfKind, thingHook, thingSound, thingSoundSet, weaponHook, type CreatureEvent, type CreatureLook, type NpcEvent, type EntityState, type Rules } from "odyssey-shared";
import { Footsteps, type SurfaceAt } from "./footsteps.ts";
import { NpcAmbience } from "./npc-sounds.ts";

/**
 * When each hook plays (ADR-085 decision 2): the page's own death, standing up, a level reached, its own acts and each
 * later blow of their chains, the hits it receives (its own hurt and death in its voice, the amendment for P1.9-T83), a refusal, the actions other players and creatures start where
 * they are drawn, a thing's change of state where it stands (the amendment for P1.9-T79), and an event on a thing
 * whose state does not change, a shrine's pulse (ADR-096 decision 28), the page's own discovery of a shrine (decision 15), an item
 * the page's own character used, as the server's `used` for its record arrives, and the potion another player drinks,
 * as its `heal` state starts (ADR-091 decision 16), and a high tier ground item the world dropped seen landing (P2.8-T77). Duels
 * and a creature's notice have their hooks named in `odyssey-shared` and no event on the page yet. The cues also pass
 * on where the listener is each frame, which the sounds they placed follow (the amendment for P1.12-T10).
 */

/**
 * Plays a hook for an actor, at an offset in tiles for a positional one; a thing's sound names the thing as its
 * owner, by which the mixer stops that thing's earlier sound (the amendment for P1.12-T9).
 */
export type PlaySound = (hook: string, actor: SoundActor, dx?: number, dy?: number, owner?: number) => boolean;

/**
 * Names where the listener is, in tiles, each frame the page's own character is drawn.
 */
export type ListenerAt = (x: number, y: number) => void;

/**
 * The sound an action a snapshot shows starting makes: an attack, a hurt, a death or a heal.
 */
export type ActionSound = "attack" | "hurt" | "death" | "heal";

/**
 * The sound a change of a character's animation bits starts: a state that changed, or the same one-shot state with its
 * restart bit flipped (ADR-074 decision 7), into an attack, a hurt, a death or a heal.
 *
 * @param previous The bits last seen.
 * @param next The bits now.
 * @returns The sound, or null when nothing starts.
 */
export function actionSound(previous: number, next: number): ActionSound | null {
  const state = characterStateOf(next);
  if (state === characterStateOf(previous) && (state === CharacterState.Dead || (previous & RESTART_BIT) === (next & RESTART_BIT))) {
    return null;
  }
  if (state === CharacterState.Attack1 || state === CharacterState.Attack2) {
    return "attack";
  }
  if (state === CharacterState.Hurt) {
    return "hurt";
  }
  if (state === CharacterState.Heal || drinkVariantOfState(state) !== null) {
    return "heal";
  }
  return state === CharacterState.Dead ? "death" : null;
}

/**
 * The actor a combatant of a kind is.
 *
 * @param kind The entity's kind, -1 when the view does not hold it.
 * @returns A creature's or an NPC's `Mob`, anything else a `Player`.
 */
function actorOfKind(kind: number): SoundActor {
  return kind === EntityKind.Creature || kind === EntityKind.Npc ? SoundActor.Mob : SoundActor.Player;
}

/**
 * What the fight tells the sound (`fight/fight.ts`).
 */
export interface FightSounds {
  /** The page's own act was sent, its swing starting, or a later blow of its chain started. */
  used(ability: number): void;
  /** A hit the page received: its ability, flags, amount and whether the page's own character took it. */
  hit(ability: number, flags: number, amount: number, source: number, taken: boolean): void;
  /** The page's own character stood up again. */
  stood(): void;
  /** An act of the page's was refused, by the server or by the page's own foresight. */
  refused(): void;
}

/**
 * The page's cues.
 */
export class SoundCues {
  /**
   * The voice of the page's own character, as its spawn record named it (ADR-085's amendment for P1.9-T83), or null
   * for none; the session sets it as snapshots arrive and clears it as it ends. Its hurt and death play in it.
   */
  public ownVoice: string | null = null;
  private readonly itemSets: (string | null)[];
  private readonly ambience: NpcAmbience;

  /**
   * @param play Plays a hook.
   * @param rules The page's rules, which name each ability by its number.
   * @param listener Names where the listener is, which every placed sound playing follows; null where nothing
   *   follows it.
   */
  public constructor(
    private readonly play: PlaySound,
    private readonly rules: Rules,
    private readonly listener: ListenerAt | null = null,
  ) {
    this.ambience = new NpcAmbience(play);
    this.itemSets = new Array<string | null>(rules.tables.abilities.length + 1).fill(null);
    for (const item of rules.tables.items) {
      const record = item.use === null ? -1 : rules.tables.abilities.findIndex((ability) => ability.id === item.use);
      if (record >= 0 && item.sound !== null && this.itemSets[record + 1] === null) {
        this.itemSets[record + 1] = item.sound;
      }
    }
  }

  /**
   * The server's `used` for the page's own character (ADR-091 decision 16): when the record is one an item's use
   * runs, the item's `use` plays, unplaced, on Player. It plays on the answer and never on the press, since a use is
   * never predicted. A record no item names, an ability's, plays nothing here: an act sounds as it is sent. A record
   * that is both on the bar and an item's use would sound at its send and again here; the rules validator refuses an
   * item whose use names a given record, so no record is both.
   *
   * @param record The record's number, as the `used` event carries it.
   */
  public itemUsed(record: number): void {
    const set = this.itemSets[record] ?? null;
    if (set !== null) {
      this.play(itemHook(set, "use"), SoundActor.Own);
    }
  }

  /**
   * An NPC sounds an event of its sound folder, placed where it stands on NPC (the amendment for P2.22-T36); the NPC is
   * the owner of the sound, so it makes one at a time and a later one takes the place of an earlier, and its idle
   * calls wait while the cue plays.
   *
   * @param sound The folder its look names under `sfx/npcs/`.
   * @param event The event.
   * @param dx Its offset across from the listener, in tiles.
   * @param dy Its offset down.
   * @param handle Its handle.
   */
  public npcCue(sound: string, event: NpcEvent, dx: number, dy: number, handle: number): void {
    this.ambience.speaking(handle);
    this.play(npcHook(sound, event), SoundActor.Npc, dx, dy, handle);
  }

  /**
   * An NPC drawn this frame, whose idle call or wing flutter sounds when its moment comes (`npc-sounds.ts`).
   *
   * @param handle The NPC's handle.
   * @param sound Its look's sound folder, null for none.
   * @param time Scene time in ms.
   * @param dx Its offset across from the listener, in tiles.
   * @param dy Its offset down.
   */
  public npcDrawn(handle: number, sound: string | null, time: number, dx: number, dy: number): void {
    this.ambience.near(handle, sound, time, dx, dy);
  }

  /**
   * Forgets the NPCs' idle timing, as a session ends.
   */
  public npcsGone(): void {
    this.ambience.clear();
  }

  /**
   * Coins landing in the page's own purse (`lootGained`'s copper): the coin pick-up plays, unplaced, as the page's own.
   * No coins play nothing.
   *
   * @param copper The copper gained.
   */
  public coinGained(copper: number): void {
    if (copper > 0) {
      this.play(CoinSound.Pickup, SoundActor.Own);
    }
  }

  /**
   * Where the page's own character is drawn this frame, which the sounds placed in the world are heard from
   * (ADR-085's amendment for P1.12-T10).
   *
   * @param x Where it is rendered across, in sixteenths of a tile.
   * @param y The same down.
   */
  public listenerAt(x: number, y: number): void {
    this.listener?.(x / SIXTEENTHS, y / SIXTEENTHS);
  }

  /**
   * The fight's sounds for a session, reading a combatant's kind from the session's view.
   *
   * @param kindOf A handle's entity kind, -1 when the view does not hold it.
   * @returns The sounds the fight calls.
   */
  public fight(kindOf: (handle: number) => number): FightSounds {
    return {
      used: (ability) => {
        const id = abilityByNumber(this.rules, ability)?.id;
        if (id !== undefined) {
          this.play(abilityHook(id, "use"), SoundActor.Own);
        }
      },
      hit: (ability, flags, amount, source, taken) => {
        const id = abilityByNumber(this.rules, ability)?.id;
        if (id !== undefined) {
          this.play(abilityHook(id, (flags & HitFlag.Critical) !== 0 ? "crit" : "hit"), taken ? actorOfKind(kindOf(source)) : SoundActor.Own);
        }
        if (taken && amount > 0) {
          this.play(playerCombatHook(this.ownVoice, "hurt"), SoundActor.Own);
        }
      },
      stood: () => {
        this.play(Stinger.Respawn, SoundActor.Own);
      },
      refused: () => {
        this.play(InterfaceSound.Refusal, SoundActor.Own);
      },
    };
  }

  /**
   * The page's own character died, as the death screen's `player_death` cue says: once per death seen live, never for
   * a character the page joined dead. The stinger is the musical cue only the player hears; the death beside it is
   * the character's, in its voice.
   */
  public died(): void {
    this.play(Stinger.Death, SoundActor.Own);
    this.play(playerCombatHook(this.ownVoice, "death"), SoundActor.Own);
  }

  /**
   * The page's footsteps, playing through these cues' sound.
   *
   * @param surfaceAt The surface under a point of the loaded map, in sixteenths of a tile.
   * @returns The footsteps.
   */
  public footsteps(surfaceAt: SurfaceAt): Footsteps {
    return new Footsteps(this.play, surfaceAt);
  }

  /**
   * A sound a vfx plays (ADR-134 decision 4): the hook of its definition for the page's own character, unplaced, or for
   * another player, placed where they stand, on Other players.
   *
   * @param hook The hook.
   * @param own Whether it is for the page's own character.
   * @param dx Their offset across from the listener, in tiles.
   * @param dy Their offset down, in tiles.
   */
  public hookPlayed(hook: string, own: boolean, dx: number, dy: number): void {
    if (own) {
      this.play(hook, SoundActor.Own);
    } else {
      this.play(hook, SoundActor.Player, dx, dy);
    }
  }

  /**
   * The page's own character discovered a shrine (ADR-096 decision 15): the minor discovery stinger, which only the
   * player hears; the major one is kept for big moments.
   */
  public discovered(): void {
    this.play(Stinger.MinorDiscovery, SoundActor.Own);
  }

  /**
   * The page's own character gained its companion: the offer accepted and the name taken (ADR-142 decision 5,
   * P2.26-T7). The stinger only the player hears.
   */
  public companionAcquired(): void {
    this.play(Stinger.CompanionAcquired, SoundActor.Own);
  }

  /**
   * An action another player or a creature starts where it is drawn. Another player's attack plays its weapon's `use`,
   * the rules' starting weapon, since every character holds it and the snapshot names no weapon; each blow of a chain
   * starts its own attack state, so each sounds. Another player's hurt and death play in its voice, the shared sounds
   * for one with none. Another player's heal plays the potion's `use` (`HEAL_SOUND_SET`), since the snapshot names no
   * item (ADR-091 decision 16). A creature has no heal sound. An NPC (ADR-136) has no action sounds: its voice is its
   * conversation's and its idle calls (`npcCue`, `npcDrawn`).
   *
   * @param kind Its entity kind.
   * @param look A creature's look, whose template names its folder; undefined for a player or an unknown look.
   * @param sound The action.
   * @param dx Its offset across from the listener, in tiles.
   * @param dy Its offset down, in tiles.
   * @param voice A player's voice, as its appearance names it; null for none and for a creature.
   */
  public action(kind: number, look: CreatureLook | undefined, sound: ActionSound, dx: number, dy: number, voice: string | null = null): void {
    if (kind === EntityKind.Creature) {
      const template = look?.template ?? look?.sheet;
      if (template !== undefined && sound !== "heal") {
        this.play(creatureHook(template, sound satisfies CreatureEvent), SoundActor.Mob, dx, dy);
      }
      return;
    }
    if (kind === EntityKind.Npc) {
      return;
    }
    if (sound === "attack") {
      this.play(weaponHook(this.rules.startingWeapon.id, "use"), SoundActor.Player, dx, dy);
    } else if (sound === "hurt") {
      this.play(playerCombatHook(voice, "hurt"), SoundActor.Player, dx, dy);
    } else if (sound === "death") {
      this.play(playerCombatHook(voice, "death"), SoundActor.Player, dx, dy);
    } else if (sound === "heal") {
      this.play(itemHook(HEAL_SOUND_SET, "use"), SoundActor.Player, dx, dy);
    }
  }

  /**
   * A high tier ground item the world dropped, seen landing (P2.8-T77, `ground/landing-sound.ts`): its tier's hook under
   * `sfx/world/loot/`, as a thing's, placed where the item lies, so it plays on World with the distance falloff of every
   * placed world sound.
   *
   * @param hook The tier's hook (`lootHook`).
   * @param dx The item's offset across from the listener, in tiles.
   * @param dy Its offset down, in tiles.
   */
  public groundLanded(hook: string, dx: number, dy: number): void {
    this.play(hook, SoundActor.Thing, dx, dy);
  }

  /**
   * A thing's change of state the page saw (ADR-085's amendment for P1.9-T79): the hook of its sound set and the verb
   * of the transition between the two states, where it stands. The set is the one its appearance names when its type
   * lists it (a gate's "Sound set", ADR-085's amendment of 2026-10-13), else its type's default. A type with no sound
   * set, a change no transition names and a state the look does not hold play nothing.
   *
   * @param kind The thing's entity kind.
   * @param look Its appearance, whose states name the two state ids; undefined when the table lacks it.
   * @param from The state index it left.
   * @param to The state index it took.
   * @param dx Its offset across from the listener, in tiles.
   * @param dy Its offset down, in tiles.
   * @param owner The thing's handle, by which its earlier sound is stopped as this one starts; none by default.
   */
  public thing(kind: number, look: ManifestObjectAppearance | undefined, from: number, to: number, dx: number, dy: number, owner?: number): void {
    const type = objectTypeOfKind(kind);
    const left = look?.states[from]?.id;
    const took = look?.states[to]?.id;
    if (type === undefined || left === undefined || took === undefined) {
      return;
    }
    const hook = thingSound(ODYSSEY_ADAPTER.objectTypes ?? [], type, left, took, look?.soundSet);
    if (hook !== null) {
      this.play(hook, SoundActor.Thing, dx, dy, owner);
    }
  }

  /**
   * A projectile landing where the page drew it land (P2.8-T84, ADR-085's note for P2.8-T84): the ability's `land`,
   * placed by the offset in tiles from the listener's position to the landing point, on World as a thing's sound is,
   * whether or not it struck anyone. Nothing plays for an ability the rules lack or while the view lacks the listener.
   *
   * @param ability The ability's number.
   * @param x The landing point across, in sixteenths.
   * @param y The landing point down, in sixteenths.
   * @param listener The page's own character's record in the view, undefined when the view lacks it.
   */
  public projectileLanded(ability: number, x: number, y: number, listener: Pick<EntityState, "x" | "y"> | undefined): void {
    const record = abilityByNumber(this.rules, ability);
    if (record !== undefined && listener !== undefined) {
      this.play(abilityHook(record.id, "land"), SoundActor.Thing, (x - listener.x) / SIXTEENTHS, (y - listener.y) / SIXTEENTHS);
    }
  }

  /**
   * A thing's change of state as the page's view holds it and the listener: `thing`, placed by the offset in tiles
   * from the listener's position to the thing's feet point, right and down positive. Nothing plays while the view
   * holds either no longer.
   *
   * @param thing The thing's record in the view, undefined when the view lacks it.
   * @param listener The page's own character's record in the view, undefined when the view lacks it.
   * @param look The thing's appearance, undefined when the table lacks it.
   * @param from The state index it left.
   * @param to The state index it took.
   * @param handle The thing's handle, which names its sound's owner; none by default.
   */
  public thingSeen(thing: Pick<EntityState, "kind" | "x" | "y"> | undefined, listener: Pick<EntityState, "x" | "y"> | undefined, look: ManifestObjectAppearance | undefined, from: number, to: number, handle?: number): void {
    if (thing !== undefined && listener !== undefined) {
      this.thing(thing.kind, look, from, to, (thing.x - listener.x) / SIXTEENTHS, (thing.y - listener.y) / SIXTEENTHS, handle);
    }
  }

  /**
   * An event on a thing whose state does not change, as the page's view holds the thing and the listener (ADR-096
   * decision 28): the hook of the thing's sound set and the event, `sfx/world/shrine/pulse` for a shrine's pop, placed
   * by the offset in tiles from the listener to the thing's feet point. A type with no sound set plays nothing, and
   * nothing plays while the view holds either no longer.
   *
   * @param thing The thing's record in the view, undefined when the view lacks it.
   * @param listener The page's own character's record in the view, undefined when the view lacks it.
   * @param event The event's name, a file name under the sound set's folder.
   * @param handle The thing's handle, which names its sound's owner; none by default.
   */
  public thingEventSeen(thing: Pick<EntityState, "kind" | "x" | "y"> | undefined, listener: Pick<EntityState, "x" | "y"> | undefined, event: string, handle?: number): void {
    const type = thing === undefined ? undefined : objectTypeOfKind(thing.kind);
    const set = type === undefined ? null : thingSoundSet(type);
    if (thing !== undefined && listener !== undefined && set !== null) {
      this.play(thingHook(set, event), SoundActor.Thing, (thing.x - listener.x) / SIXTEENTHS, (thing.y - listener.y) / SIXTEENTHS, handle);
    }
  }
}
